import { useClientConfigModelStore } from '@/models/client-config/clientConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { RequestDocsSiteCloneDataType } from '@/services/docs-site-clone/requestDocsSiteClone';
import { requestDocsSiteClone } from '@/services/docs-site-clone/requestDocsSiteClone';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { usePersistFn } from 'ahooks';
import { Button, notification } from 'aui';
import type React from 'react';
import { useEffect, useRef } from 'react';

export type TProps = {
  onCancel?: () => void;
};

const CloneProjectModal: React.FC<TProps> = (props: TProps) => {
  const { onCancel } = props;
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title) || '';
  const { onlineId, onlineType, teamId } = useDocsDataModelStore((x) => x.docsIdTypeData);
  const projectId = useDocsDataModelStore((x) => x.docsIdTypeData.projectId);
  const branchId = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return undefined;
    }
    return x.docsIdTypeData.branchId;
  });
  const subdirectory = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return '';
    }
    return x.docsIdTypeData.subdirectory;
  });
  const visitType = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return DocsSiteVisitType.WithId;
    }
    return x.docsIdTypeData.visitType;
  });
  const envConfig = useEnvConfigModelStore((x) => x);
  const appWebUrl = useClientConfigModelStore((x) => x.appWebUrl);
  const src = IS_PDV
    ? `${appWebUrl}/web/online-share/clone?onlineShareName=${title}&teamId=${teamId}`
    : `${appWebUrl}/online-share/clone?onlineShareName=${title}&teamId=${teamId}`;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const notificationClose = usePersistFn((myNotification: any, key: any, cloneProjectId: any) => {
    myNotification.close(key);
    window.open(
      IS_PDV
        ? `${appWebUrl}/web/project/${cloneProjectId}`
        : `${appWebUrl}/project/${cloneProjectId}`,
    );
  });

  const openNotification = (teamName: string, cloneProjectId: number | undefined) => {
    const key = `open${Date.now()}`;
    const btn = (
      <Button type="primary" onClick={() => notificationClose(notification, key, cloneProjectId)}>
        {clientI18n('projectDetail.label.lookDetailButton')}
      </Button>
    );

    notification.open({
      message: clientI18n('projectDetail.label.lookDetailButton'),
      description: (
        <span
          dangerouslySetInnerHTML={{
            __html: clientI18n('onlineShareHasBeenCopyToTeam', {
              onlineShareName: title,
              teamName,
            }),
          }}
        />
      ),
      duration: null,
      btn,
      key,
    });
  };

  const cloneDocsSite = async (requestData: RequestDocsSiteCloneDataType) => {
    const [, result] = await requestDocsSiteClone(
      requestData,
      onlineType,
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    if (result) {
      onCancel?.();
      openNotification(result.teamName, result.projectId);
    }
  };

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'HANDLE_CLONE') {
        const { teamId: selectedTeamId, userId } = event.data.payload;
        if (!onlineId) {
          return;
        }
        const requestData: RequestDocsSiteCloneDataType = {
          teamId: selectedTeamId,
          userId,
          id: onlineId,
          projectId,
          branchId,
        };

        if (onlineType === OnlineType.DocsSite) {
          requestData.branchId = branchId;
        }

        cloneDocsSite(requestData);
      }

      if (event.data?.type === 'HANDLE_CANCEL') {
        onCancel?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onlineId, branchId, onlineType, projectId, onCancel]);

  return (
    <div style={{ height: '640px', overflow: 'hidden' }}>
      <iframe
        title="clone"
        ref={iframeRef}
        className="h-full w-full"
        src={src}
        frameBorder={0}
        scrolling="no"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads allow-modals"
        allow="cross-origin-isolated"
      />
    </div>
  );
};

export default CloneProjectModal;
